import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { AppShell, IncidentLayout } from './components/layout';
import { UserProvider, AIAssistantUIProvider, useUser } from './context';
import {
  Dashboard,
  IncidentList,
  IncidentDetail,
  IncidentInvestigation,
  IncidentKqlQuery,
  TenantList,
  TenantOverview,
  Settings,
  IndustryUpdates,
  ReportGenerate,
  ReportPrint,
  Reports,
  Notebook,
  MeetingRetros,
  Autopilot,
} from './pages';
import { ShieldX, LogOut, SearchX } from 'lucide-react';

function NotFound() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 bg-bg-tertiary rounded-full flex items-center justify-center mx-auto mb-6">
          <SearchX className="w-8 h-8 text-text-secondary" />
        </div>
        <h1 className="text-2xl font-semibold text-text-primary mb-2">
          Page not found
        </h1>
        <p className="text-text-secondary mb-6">
          The page you're looking for doesn't exist or has moved.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-navy dark:bg-brand-lime text-white dark:text-brand-navy rounded-lg hover:bg-navy-hover dark:hover:bg-lime-hover transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

function AccessDenied({ error, email, onLogout }: { error: string; email?: string; onLogout: () => void }) {
  return (
    <div className="min-h-screen bg-bg-tertiary flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-bg-secondary rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldX className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-2xl font-semibold text-text-primary mb-2">
          Access Denied
        </h1>
        <p className="text-text-secondary mb-6">
          {error}
        </p>
        {email && (
          <p className="text-sm text-text-secondary mb-6">
            Logged in as: <span className="font-medium">{email}</span>
          </p>
        )}
        <button
          onClick={onLogout}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-navy dark:bg-brand-lime text-white dark:text-brand-navy rounded-lg hover:bg-navy-hover dark:hover:bg-lime-hover transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-bg-tertiary flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-text-secondary">Authenticating...</p>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { isLoading, error, isAuthenticated, swaUser, logout } = useUser();

  // Show loading while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show error if user auth failed (e.g., not in internal users list)
  if (error) {
    return (
      <AccessDenied 
        error={error} 
        email={swaUser?.userDetails} 
        onLogout={logout} 
      />
    );
  }

  // Show access denied if authenticated with Azure AD but no matching user
  if (!isAuthenticated && swaUser) {
    return (
      <AccessDenied 
        error="Your account is not authorized to access this application." 
        email={swaUser.userDetails} 
        onLogout={logout} 
      />
    );
  }

  return (
    <AIAssistantUIProvider>
      <Routes>
        {/* Standard pages with AppShell */}
        <Route path="/" element={<AppShell><Dashboard /></AppShell>} />
        <Route path="/incidents" element={<AppShell><IncidentList /></AppShell>} />
        <Route path="/tenants" element={<AppShell><TenantList /></AppShell>} />
        <Route path="/tenants/:tenantId" element={<AppShell><TenantOverview /></AppShell>} />
        <Route path="/updates" element={<AppShell><IndustryUpdates /></AppShell>} />
        <Route path="/reports/generate" element={<AppShell><ReportGenerate /></AppShell>} />
        <Route path="/reports" element={<AppShell><Reports /></AppShell>} />
        <Route path="/reports/:reportId" element={<AppShell><Reports /></AppShell>} />
        <Route path="/retros" element={<AppShell><MeetingRetros /></AppShell>} />
        <Route path="/retros/:tenantId" element={<AppShell><MeetingRetros /></AppShell>} />
        <Route path="/retros/:tenantId/:retroId" element={<AppShell><MeetingRetros /></AppShell>} />
        <Route path="/notebook" element={<AppShell><Notebook /></AppShell>} />
        <Route path="/notebook/:tenantId" element={<AppShell><Notebook /></AppShell>} />
        <Route path="/settings" element={<AppShell><Settings /></AppShell>} />
        <Route path="/autopilot" element={<AppShell><Autopilot /></AppShell>} />
        
        {/* Incident pages with AI Assistant context */}
        <Route path="/incidents/:tenantId/:incidentId" element={<IncidentLayout />}>
          <Route index element={<IncidentDetail />} />
          <Route path="investigate" element={<IncidentInvestigation />} />
          <Route path="kql" element={<IncidentKqlQuery />} />
        </Route>

        {/* Unknown authenticated routes: explicit 404 instead of a blank page */}
        <Route path="*" element={<AppShell><NotFound /></AppShell>} />
      </Routes>
    </AIAssistantUIProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Chrome-less magazine render driven by func-axn-report-pdf.
            Deliberately OUTSIDE both the auth gate AND UserProvider:
            UserProvider hard-redirects unauthenticated visitors to
            /.auth/login (UserContext), which would bounce headless Chromium
            off the page before it could render. The report payload arrives
            injected as window.__REPORT_DATA__; without it the route renders
            only a loading placeholder (its API fallback still requires an
            authenticated session). */}
        <Route path="/reports/:reportId/print" element={<ReportPrint />} />
        <Route
          path="*"
          element={
            <UserProvider>
              <AuthenticatedApp />
            </UserProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
